import { createSelector } from '@reduxjs/toolkit';
import { selectBankPhotoFilter } from 'scenes/Nerobotti/store/nerobottiSlice';
import processTree from 'scenes/ProjectBank/components/processTree';
import { selectIsBankGalleryOnly } from 'scenes/ProjectBank/store/selectors/project-bank-access.selectors';
import { isBankMediaRow } from 'scenes/ProjectBank/utils/bankAccess';
import {
  createProjectBankFormsRootFolder,
  isProjectTypeProjectBankFolder,
} from 'scenes/ProjectBank/utils/projectBankForms';
import { selectProjectUsers } from 'store/projectUsers/selectors/projectUsers.selectors';
import {
  selectProjects,
  selectSelectedProjectId,
} from 'store/projects/selectors/projectsSelectors';

const selectProjectBankState = (state) => state.projectBank;

export const selectIsProjectTreeLoading = createSelector(
  [selectProjectBankState],
  (state) => state.projectTreeLoading
);

const selectProjectBankBaseFilesFilePermissions = createSelector(
  [selectProjectBankState],
  (state) => state.filesFilePermissions.entities
);

const selectProjectBankFormsVirtualFilePermissions = createSelector(
  [selectProjectBankState],
  (state) => state.projectBankFormsFiles.entities
);

export const selectProjectBankFilesFilePermissions = createSelector(
  [
    selectProjectBankBaseFilesFilePermissions,
    selectProjectBankFormsVirtualFilePermissions,
  ],
  (baseFiles, virtualFiles) => ({
    ...baseFiles,
    ...virtualFiles,
  })
);

export const selectSelectedProjectBankFolderPath = createSelector(
  [selectProjectBankState],
  (state) => state.selectedFolderPath
);

export const selectSelectedProjectBankFolderId = createSelector(
  [selectProjectBankState],
  (state) => state.selectedFolderId
);

export const selectProjectBankSortBy = createSelector(
  [selectProjectBankState],
  (state) => state.sortBy
);

export const selectSelectedTilaajavastuuBusinessId = createSelector(
  [selectProjectBankState],
  (state) => state.selectedTilaajavastuuBusinessId
);

export const selectIsLoadingTilaajavastuuDocuments = createSelector(
  [selectProjectBankState],
  (state) => state.isLoadingTilaajavastuuDocuments
);
export const selectSelectedPreviewTilaajavastuuDocument = createSelector(
  [selectProjectBankState],
  (state) => state.selectedPreviewTilaajavastuuDocument
);

export const selectTilaajavastuuDocumentEntities = createSelector(
  [selectProjectBankState],
  (state) => state.tilaajavastuuDocuments.entities
);

export const selectSelectedTilaajavastuuFolderProjectId = createSelector(
  [selectProjectBankState],
  (state) => state.selectedTilaajavastuuFolderProjectId
);

export const selectOpenedProjectTreeFolderIds = createSelector(
  [selectProjectBankState],
  (state) => state.openedProjectTreeFolderIds
);
export const selectOpenedPrivateProjectBankTreeFolderIds = createSelector(
  [selectProjectBankState],
  (state) => state.openedPrivateProjectBankTreeFolderIds
);

/** Id of the folder whose tree row waits to be scrolled into view, or null. */
export const selectRevealedProjectBankTreeFolderId = createSelector(
  [selectProjectBankState],
  (state) => state.revealedTreeFolderId || null
);

export const selectIsPdfPreviewDialogOpen = createSelector(
  [selectProjectBankState],
  (state) => state.isPdfPreviewDialogOpen
);

export const selectProjectBankViewMode = createSelector(
  [selectProjectBankState],
  (state) => state.projectBankViewMode
);

export const selectSelectedProjectBankFiles = createSelector(
  [selectProjectBankState],
  (state) => state.selectedProjectBankFiles
);

export const selectIsProjectBankFileSelectDialogOpen = createSelector(
  [selectProjectBankState],
  (state) => state.isProjectBankFileSelectDialogOpen
);

export const selectIsTransferFolderStructureDialogOpen = createSelector(
  [selectProjectBankState],
  (state) => state.isTransferFolderStructureDialogOpen
);

export const selectIsAddFolderDialogOpen = createSelector(
  [selectProjectBankState],
  (state) => state.isAddFolderDialogOpen
);

export const selectIsAddingFolder = createSelector(
  [selectProjectBankState],
  (state) => state.isAddingFolder
);

export const selectTilaajavastuuIntegrationStatusesByBusinessId =
  createSelector(
    [selectProjectBankState],
    (state) => state.tilaajavastuuIntegrationStatusesByBusinessId
  );

export const selectTilaajavastuuDocumentsArray = createSelector(
  [selectTilaajavastuuDocumentEntities],
  (entities) => Object.values(entities)
);

export const selectIsLoadingFolderStructureTransfer = createSelector(
  [selectProjectBankState],
  (state) => state.isLoadingFolderStructureTransfer
);

export const selectSelectedProjectBankVisibilityMode = createSelector(
  [selectProjectBankState],
  (state) => state.selectedProjectBankVisibilityMode
);

export const selectSelectedProjectBankDataTransferTargetVisibilityMode =
  createSelector(
    [selectProjectBankState],
    (state) => state.selectedProjectBankDataTransferTargetVisibilityMode
  );

export const selectFoundTilaajavastuuDocumentsArray = createSelector(
  [selectTilaajavastuuDocumentsArray],
  (tilaajavastuuDocuments) =>
    tilaajavastuuDocuments.filter((document) => document.status !== 'NOT_FOUND')
);

export const selectInsufficientTilaajavastuuDocumentsBusinessIdsArrayByProjectId =
  createSelector(
    [selectFoundTilaajavastuuDocumentsArray, (_, args) => args],
    (tilaajavastuuDocuments, args) => {
      const notAlarmedStatuses = ['Ok', 'Tietoja odotetaan'];
      return tilaajavastuuDocuments
        .filter(
          (document) =>
            !notAlarmedStatuses.includes(document.status) &&
            document.projectId === args &&
            !document.deletedAt
        )
        .map((document) => document.businessId.split('#')[0]);
    }
  );

export const selectProjectBankFilesFilePermissionsArray = createSelector(
  [selectProjectBankFilesFilePermissions],
  (filePermissions) =>
    Object.values(filePermissions).filter(
      (filePermission) => !filePermission.deletedAt
    )
);

const selectProjectFolderFilePermissionsBase = createSelector(
  [selectProjectBankState],
  (state) => state.projectFolderFilePermissions.entities
);

const selectProjectFolderFilePermissionsBaseArray = createSelector(
  [selectProjectFolderFilePermissionsBase],
  (filePermissions) =>
    Object.values(filePermissions).filter(
      (filePermission) => !filePermission.deletedAt
    )
);

// The forms are the bank's, not the gallery's: a gallery-only user
// (utils/bankAccess) gets no forms folder, so no forms row or card is drawn
// for them.
const selectProjectBankFormsRootFolders = createSelector(
  [
    selectProjectFolderFilePermissionsBaseArray,
    selectProjects,
    selectIsBankGalleryOnly,
  ],
  (folderFilePermissions, projects, isGalleryOnly) =>
    (isGalleryOnly ? [] : folderFilePermissions)
      .filter((folder) => {
        if (!isProjectTypeProjectBankFolder(folder)) return false;
        const projectId =
          typeof folder.project === 'object'
            ? folder.project?.id
            : folder.project;
        const project = projectId ? projects[projectId] : null;
        if (
          project &&
          project.businessTypeEnum === 'EMPLOYER' &&
          !project.masterParent
        ) {
          return false;
        }
        return true;
      })
      .map((folder) => createProjectBankFormsRootFolder(folder))
      .filter(Boolean)
);

const selectProjectBankFormsVirtualFolderPermissions = createSelector(
  [selectProjectBankState],
  (state) => state.projectBankFormsFolders.entities
);

export const selectProjectBankFormsVirtualFolderPermissionsArray =
  createSelector(
    [selectProjectBankFormsVirtualFolderPermissions],
    (filePermissions) =>
      Object.values(filePermissions).filter(
        (filePermission) => !filePermission.deletedAt
      )
  );

export const selectProjectBankFormsVirtualFilesArray = createSelector(
  [selectProjectBankFormsVirtualFilePermissions],
  (filePermissions) =>
    Object.values(filePermissions).filter(
      (filePermission) => !filePermission.deletedAt
    )
);

export const selectProjectFolderFilePermissionsArray = createSelector(
  [
    selectProjectFolderFilePermissionsBaseArray,
    selectProjectBankFormsRootFolders,
    selectProjectBankFormsVirtualFolderPermissionsArray,
  ],
  (baseFolders, formsRootFolders, virtualFolders) => [
    ...baseFolders,
    ...formsRootFolders,
    ...virtualFolders,
  ]
);

export const selectProjectFolderFilePermissions = createSelector(
  [selectProjectFolderFilePermissionsArray],
  (filePermissions) =>
    filePermissions.reduce((acc, filePermission) => {
      acc[filePermission.id] = filePermission;
      return acc;
    }, {})
);

export const selectSelectedProjectBankFolder = createSelector(
  [selectProjectFolderFilePermissions, selectSelectedProjectBankFolderId],
  (folders, selectedFolderId) => folders[selectedFolderId]
);

/**
 * Ids of every project whose folders the open bank holds: the project itself
 * and the sub-projects whose folders sit in its tree. What "this bank shows
 * that project" means for a filter that names one.
 * @param {Object} state redux state
 * @returns {Set<string>}
 */
export const selectBankProjectIds = createSelector(
  [selectProjectFolderFilePermissionsBaseArray],
  (folders) =>
    new Set(folders.map((folder) => getItemProjectId(folder)).filter(Boolean))
);

const EMPTY_FILE_ID_SET = new Set();

/**
 * The photo filter the AI conversation put on the bank, when it applies
 * here: PUBLIC mode, on a project this bank shows. The filter itself lives
 * with the conversation (see the Nerobotti slice); this is where the bank
 * decides whether to show it.
 * @param {Object} state redux state
 * @returns {?Object} `{ id, label, fileIds, total, truncated, criteria, ... }`
 */
export const selectBankAiFilter = createSelector(
  [
    selectBankPhotoFilter,
    selectBankProjectIds,
    selectSelectedProjectBankVisibilityMode,
  ],
  (filter, projectIds, mode) =>
    filter && mode === 'PUBLIC' && projectIds.has(filter.projectId)
      ? filter
      : null
);

/**
 * File ids the AI filter keeps; an empty, stable Set while none applies.
 * @param {Object} state redux state
 * @returns {Set<string>}
 */
export const selectBankAiFilteredFileIds = createSelector(
  [selectBankAiFilter],
  (filter) => (filter ? new Set(filter.fileIds) : EMPTY_FILE_ID_SET)
);

const EMPTY_FOLDER_ID_SET = new Set();

/**
 * Folders the AI filter keeps: the ones its photos are in and every folder
 * above them, up to the root.
 *
 * Without the ancestors the filter empties the root — the photos are three
 * levels down, and the folder that would lead to them is not itself a match.
 * The server sends the folders the photos are *in* (`matchFolderIds`, one per
 * row, duplicate `images` rows and all); the walk up is done here because the
 * tree is what the bank has and the server does not.
 *
 * `parent` is the link; `parentPath` is the fallback for a row that carries
 * only that, which is how the bank's own child lookup reads them too.
 * @param {Object} state redux state
 * @returns {Set<string>} folder ids, empty and stable while no filter applies
 */
export const selectBankAiFilteredFolderIds = createSelector(
  [selectBankAiFilter, selectProjectFolderFilePermissions],
  (filter, foldersById) => {
    const matched = Array.isArray(filter?.matchFolderIds)
      ? filter.matchFolderIds
      : [];
    if (!matched.length) return EMPTY_FOLDER_ID_SET;

    const idByPath = {};
    Object.values(foldersById).forEach((folder) => {
      if (folder?.path && !idByPath[folder.path]) {
        idByPath[folder.path] = folder.id;
      }
    });

    const keep = new Set();
    matched.forEach((matchedId) => {
      let id = matchedId;
      // Stops at the root, at a parent the bank has not loaded, and at a
      // folder already kept — which is also what breaks a cycle.
      while (id && !keep.has(id)) {
        keep.add(id);
        const folder = foldersById[id];
        id =
          folder?.parent || (folder?.parentPath && idByPath[folder.parentPath]);
      }
    });
    return keep;
  }
);

export const selectSelectedPrivateProjectBankFolderId = createSelector(
  [selectProjectBankState],
  (state) => state.selectedPrivateProjectBankFolderId
);

export const selectPrivateProjectBankItemsEntities = createSelector(
  [selectProjectBankState],
  (state) => state.privateProjectBankItems.entities
);

export const selectSelectedPrivateProjectBankFolderItem = createSelector(
  [
    selectPrivateProjectBankItemsEntities,
    selectSelectedPrivateProjectBankFolderId,
  ],
  (privateProjectBankEntities, selectedFolderId) =>
    privateProjectBankEntities
      ? privateProjectBankEntities[selectedFolderId]
      : undefined
);

export const selectPrivateProjectBankItemsArray = createSelector(
  [selectPrivateProjectBankItemsEntities],
  (entities) => Object.values(entities)
);

export const selectSelectedProjectPrivateProjectBankItems = createSelector(
  [selectPrivateProjectBankItemsArray, selectSelectedProjectId],
  (projectBankItems, selectedProjectId) =>
    projectBankItems.filter(
      (item) => item.projectId === selectedProjectId && !item.deletedAt
    )
);

export const selectSelectedProjectPrivateProjectBankFolders = createSelector(
  [selectSelectedProjectPrivateProjectBankItems],
  (items) => items.filter((item) => item.type === 'FOLDER')
);

export const selectSelectedProjectPrivateProjectBankFiles = createSelector(
  [selectSelectedProjectPrivateProjectBankItems],
  (items) => items.filter((item) => item.type === 'FILE')
);

export const selectProjectFolderFilePermissionsArrayWithJoinedData =
  createSelector(
    [
      selectProjectFolderFilePermissionsArray,
      selectProjects,
      selectProjectUsers,
    ],
    (filePermissions, projects, users) =>
      filePermissions.map((filePermission) => ({
        ...filePermission,
        project:
          typeof filePermission.project === 'object'
            ? filePermission.project
            : projects[filePermission.project]
              ? projects[filePermission.project]
              : { id: filePermission.project },
        user:
          typeof filePermission.user === 'object'
            ? filePermission.user
            : users[filePermission.user]
              ? users[filePermission.user]
              : { id: filePermission.user, firstName: '', lastName: '' },
      }))
  );

// Each file's joined object, kept for as long as the row and what it was
// joined with stay the same (see below).
const joinedFilesByRow = new WeakMap();

/**
 * The files with their uploader and project joined in. A row that has not
 * changed, nor the user or project it was joined with, keeps the very object
 * it got before: the gallery's photos land a page at a time, and a page, a
 * file size or a reaction landing must not make every other row look new to
 * what goes by its identity (an open viewer's loads, the memoized cards).
 */
export const selectProjectBankFilesFilePermissionsArrayWithJoinedData =
  createSelector(
    [
      selectProjectBankFilesFilePermissionsArray,
      selectProjectUsers,
      selectProjects,
    ],
    (files, users, projects) =>
      files.map((file) => {
        const userRef =
          typeof file.user === 'object' ? file.user : users[file.user];
        const projectRef =
          typeof file.project === 'object'
            ? file.project
            : projects[file.project];
        const cached = joinedFilesByRow.get(file);
        if (
          cached &&
          cached.userRef === userRef &&
          cached.projectRef === projectRef
        ) {
          return cached.joined;
        }
        const joined = {
          ...file,
          user:
            typeof file.user === 'object'
              ? file.user
              : userRef
                ? userRef
                : { id: file.user, firstName: '', lastName: '' },
          project:
            typeof file.project === 'object'
              ? file.project
              : projectRef
                ? projectRef
                : { id: file.project },
        };
        joinedFilesByRow.set(file, { joined, userRef, projectRef });
        return joined;
      })
  );

/** Project id of a folder / file whose `project` is an id or a joined object. */
const getItemProjectId = (item) => {
  if (!item || !item.project) return null;
  return typeof item.project === 'object' ? item.project.id : item.project;
};

const EMPTY_FILES = [];

/**
 * The gallery view's list: every loaded photo and video of the bank's
 * project, from any of its folders or from none (a bulletin's, a form's),
 * joined as a folder's files are; unsorted. loadProjectBankGalleryMedia
 * brings them all, and an upload or the images folder's own load adds to
 * them. Other projects' files (a sub-project's) are not the gallery's.
 * @param {Object} state redux state
 * @returns {Array<Object>} joined FilePermissions
 */
export const selectProjectBankGalleryMediaFiles = createSelector(
  [
    selectProjectBankFilesFilePermissionsArrayWithJoinedData,
    selectSelectedProjectId,
  ],
  (files, projectId) =>
    projectId
      ? files.filter(
          (file) => getItemProjectId(file) === projectId && isBankMediaRow(file)
        )
      : EMPTY_FILES
);

/**
 * How far the gallery view's list of the bank's project has loaded: 'idle'
 * (nothing asked, or asked for another project), 'loading', 'loaded' or
 * 'failed'.
 * @param {Object} state redux state
 * @returns {string}
 */
export const selectProjectBankGalleryMediaStatus = (state) => {
  const galleryMedia = state.projectBank && state.projectBank.galleryMedia;
  if (!galleryMedia) return 'idle';
  return galleryMedia.projectId &&
    galleryMedia.projectId === selectSelectedProjectId(state)
    ? galleryMedia.status
    : 'idle';
};

export const selectSelectedProjectBankFolderContentFiles = createSelector(
  [
    selectSelectedProjectBankFolder,
    selectProjectBankFilesFilePermissionsArrayWithJoinedData,
    selectProjectFolderFilePermissionsArray,
  ],
  (selectedFolder, files, folders) => {
    if (!selectedFolder) return [];
    if (selectedFolder.name === 'images') {
      // A project can hold several `images` system folders; the open one
      // shows the direct children of all of them. Only direct children: the
      // old path-substring test also pulled in every file of their
      // subfolders once those had been loaded, so a subfolder's images
      // showed up in the parent after visiting it.
      //
      // A photo saved from a form or with the camera button of the Projects
      // page has no `parent`, only the images folder's key and path: the
      // backend lists the images folder by key, so it is here all the same,
      // and it belongs to the folder whose key or path it names.
      const projectId = getItemProjectId(selectedFolder);
      const imagesFolderIds = new Set([selectedFolder.id]);
      const imagesFolderKeys = new Set();
      const imagesFolderPaths = new Set();
      [selectedFolder, ...folders].forEach((folder) => {
        if (
          folder &&
          folder.name === 'images' &&
          getItemProjectId(folder) === projectId
        ) {
          imagesFolderIds.add(folder.id);
          if (folder.key) imagesFolderKeys.add(folder.key);
          if (folder.path) imagesFolderPaths.add(folder.path);
        }
      });
      return files.filter(
        (file) =>
          file &&
          (file.parent
            ? imagesFolderIds.has(file.parent)
            : imagesFolderKeys.has(file.parentKey) ||
              imagesFolderPaths.has(file.parentPath))
      );
    }
    return files.filter((file) => file && file.parent === selectedFolder.id);
  }
);

export const selectSelectedPrivateProjectBankFolderContentFiles =
  createSelector(
    [
      selectSelectedPrivateProjectBankFolderId,
      selectSelectedProjectPrivateProjectBankFiles,
    ],
    (selectedPrivateFolderId, privateProjectBankFiles) =>
      selectedPrivateFolderId
        ? privateProjectBankFiles.filter(
            (file) => file.parentFolderId === selectedPrivateFolderId
          )
        : privateProjectBankFiles.filter((file) => !file.parentFolderId)
  );

export const selectSelectedPrivateProjectBankFolderContentFolders =
  createSelector(
    [
      selectSelectedPrivateProjectBankFolderId,
      selectSelectedProjectPrivateProjectBankFolders,
    ],
    (selectedPrivateFolderId, privateProjectBankFolders) =>
      selectedPrivateFolderId
        ? privateProjectBankFolders.filter(
            (folder) => folder.parentFolderId === selectedPrivateFolderId
          )
        : privateProjectBankFolders.filter((folder) => !folder.parentFolderId)
  );

export const selectSelectedProjectBankFolderChildFolders = createSelector(
  [
    selectProjectFolderFilePermissionsArrayWithJoinedData,
    selectSelectedProjectBankFolderId,
    selectSelectedProjectBankFolderPath,
  ],
  (folderFilePermissions, selectedFolderId, selectedFolderPath) =>
    folderFilePermissions.filter(
      (folder) =>
        folder.parent === selectedFolderId ||
        folder.parentPath === selectedFolderPath
    )
);

export const selectSelectedProjectBankFolderChildProjectFolders =
  createSelector([selectSelectedProjectBankFolderChildFolders], (folders) => {
    const childProjects = folders.filter(
      (folder) =>
        (folder.businessId && folder.key.split('/').length === 3) ||
        (!folder.businessId && folder.key.split('/').length === 2)
    );

    return Object.values(processTree(childProjects));
  });

export const selectSelectedProjectBankFolderChildNormalFolders = createSelector(
  [selectSelectedProjectBankFolderChildFolders],
  (folders) =>
    folders.filter(
      (folder) =>
        (folder.businessId && folder.key.split('/').length > 3) ||
        (!folder.businessId && folder.key.split('/').length > 2)
    )
);

export const selectIsFolderFilesLoading = createSelector(
  [selectProjectBankState],
  (state) => state.isFolderFilesLoading
);

/**
 * Whether an `updateProjectBankItem` (rename, description, rotation) is in
 * flight — the save spinner of the media viewer.
 * @param {Object} state redux state
 * @returns {boolean}
 */
export const selectIsProjectBankUpdateLoading = createSelector(
  [selectProjectBankState],
  (state) => !!state.updateLoading
);

export const selectInitialProjectBankPath = createSelector(
  [selectProjectBankState],
  (state) => state.initialProjectBankPath
);

export const selectProjectBankProjectTree = createSelector(
  [
    selectProjectFolderFilePermissionsArrayWithJoinedData,
    selectInitialProjectBankPath,
  ],
  (folderFilePermissions, urlPath) => {
    const filteredProjects = folderFilePermissions.filter((folder) =>
      folder.path?.startsWith(urlPath)
    );
    const tree = processTree(filteredProjects, urlPath);

    return tree;
  }
);

export const selectFolderTree = createSelector(
  [selectProjectBankProjectTree, selectInitialProjectBankPath],
  (projectTree, urlPath) => {
    const treeFolders = Object.values(projectTree);

    const rootFolder = treeFolders.find((folder) => folder.path === urlPath);

    return rootFolder;
  }
);

export const selectTransferFolderStructureTargetProjectId = createSelector(
  [selectProjectBankState],
  (state) => state.transferFolderStructureTargetProjectId
);

export const selectTransferFolderStructureTargetProject = createSelector(
  [selectProjects, selectTransferFolderStructureTargetProjectId],
  (projects, targetProjectId) => projects[targetProjectId]
);

const getDescendantFolders = (folderId, allFolders) => {
  const directChildren = allFolders.filter(
    (folder) => folder.parent === folderId
  );

  let descendants = [...directChildren];

  for (const child of directChildren) {
    const childDescendants = getDescendantFolders(child.id, allFolders);
    descendants = [...descendants, ...childDescendants];
  }

  return descendants;
};

const buildFolderTreeWithChildren = (folderId, allFolders, allFiles) => {
  const folder = allFolders.find((f) => f.id === folderId);

  if (!folder) {
    return null;
  }

  // Get direct child folders
  const childFolders = allFolders.filter((f) => f.parent === folderId);

  // Get direct child files
  const childFiles = allFiles.filter((f) => f.parent === folderId);

  // Recursively build tree for child folders
  const childFoldersWithChildren = childFolders.map((childFolder) =>
    buildFolderTreeWithChildren(childFolder.id, allFolders, allFiles)
  );

  return {
    ...folder,
    type: 'FOLDER',
    children: [...childFiles.map((file) => file), ...childFoldersWithChildren],
  };
};

export const selectProjectBankFolderTree = createSelector(
  [
    (state, folderId) => folderId,
    selectProjectFolderFilePermissionsArrayWithJoinedData,
    selectProjectBankFilesFilePermissionsArrayWithJoinedData,
  ],
  (folderId, allFolders, allFiles) => {
    if (!folderId) {
      return { folders: [], files: [], allItems: [] };
    }

    // Get all descendant folders recursively
    const descendantFolders = getDescendantFolders(folderId, allFolders);
    const descendantFolderIds = new Set([
      folderId,
      ...descendantFolders.map((f) => f.id),
    ]);

    // Get all files that belong to any of these folders
    const descendantFiles = allFiles.filter((file) =>
      descendantFolderIds.has(file.parent)
    );

    const tree = buildFolderTreeWithChildren(folderId, allFolders, allFiles);

    return {
      folders: descendantFolders,
      files: descendantFiles,
      allItems: [...descendantFolders, ...descendantFiles],
      tree,
    };
  }
);

/**
 * Get full folder tree (folders and files) for the selected folder
 */
export const selectSelectedProjectBankFolderFullTree = createSelector(
  [selectProjectBankFolderTree],
  (tree) => tree.tree
);

export const selectSelectedFilesByIds = createSelector(
  [selectProjectBankFilesFilePermissions, (_, fileIds) => fileIds],
  (allFiles, fileIds = []) =>
    (fileIds || []).map((id) => allFiles[id]).filter((file) => !!file)
);

export const selectSelectedProjectBankFileObjects = createSelector(
  [selectSelectedProjectBankFiles, selectProjectBankFilesFilePermissionsArray],
  (selectedIds, filePermissions) =>
    filePermissions.filter((fp) => selectedIds.includes(fp.id))
);

export const selectPrivateProjectFoldersNavigationPath = createSelector(
  [
    selectSelectedPrivateProjectBankFolderItem,
    selectPrivateProjectBankItemsEntities,
  ],
  (selectedPrivateProjectBankFolder, projectBankItemEntities) => {
    const pathFoldersIds = selectedPrivateProjectBankFolder
      ? selectedPrivateProjectBankFolder.path.split('/')
      : [];

    const pathFolders = [];

    pathFoldersIds.forEach((folderId) => {
      if (projectBankItemEntities[folderId]) {
        pathFolders.push(projectBankItemEntities[folderId]);
      }
    });

    return pathFolders;
  }
);

export const selectSelectedPrivateProjectBankItemIds = createSelector(
  [selectProjectBankState],
  (state) => state.selectedPrivateProjectBankItemIds
);

/**
 * Active bank filters `{ formatKeys, authorIds, dateRange, mediaTagIds,
 * mediaTagMatch, floorplanId, floorplanArea }` (utils/bankItemFilter
 * EMPTY_BANK_FILTERS).
 */
export const selectBankFilters = createSelector(
  [selectProjectBankState],
  (state) => state.bankFilters
);

/** Raw search text typed into the bank search field. */
export const selectBankSearchText = createSelector(
  [selectProjectBankState],
  (state) => state.bankSearchText
);

/** Whether the bank filter panel is expanded. */

/** Cached public file sizes by file permission id (null = unresolvable). */
export const selectFileSizesById = createSelector(
  [selectProjectBankState],
  (state) => state.fileSizesById
);

/** File permission ids whose size request is in flight. */
export const selectPendingFileSizeIds = createSelector(
  [selectProjectBankState],
  (state) => state.pendingFileSizeIds
);

/** The business's bank storage usage (loadProjectBankStorageUsage), or null. */
export const selectProjectBankStorageUsage = createSelector(
  [selectProjectBankState],
  (state) => state.storageUsage || null
);

/** 'idle' | 'loading' | 'loaded' | 'failed' */
export const selectProjectBankStorageUsageStatus = createSelector(
  [selectProjectBankState],
  (state) => state.storageUsageStatus || 'idle'
);
